import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, IconButton, InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar, fieldValidations, FormProvider, Iconify, RHFTextField, useBoolean } from '@zeta-core';
import { getErrorMessage, UserServiceAPIs } from '@zeta-services';
import { IGenericErrorResponse, ISubmitResetPasswordRequest, IUserLogin2FASendRequest, IUserLoginRequest, IUserLoginResponse } from '@zeta-types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { object } from 'yup';

import { CommonsConfig } from '../../../../lib/contexts';
import { PortalNameType } from '../../../../lib/types';
import { trackEvent } from '../../../helpers/track-event';

interface ILogin {
  userNameOrEmail: string;
  password: string;
}

interface ICommonLoginProps {
  registrationAllowed?: boolean;
}

export const LoginPageCommon = ({ registrationAllowed }: ICommonLoginProps) => {
  const navigate = useNavigate();
  const password = useBoolean();
  const theme = useTheme();
  const { portal } = CommonsConfig();

  const [loginDataToBePassed, setLoginDataToBePassed] = useState<IUserLoginResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [isResetPassword, setIsResetPassword] = useState<boolean>(false);

  const verification2FaSendApi = useMutation({
    mutationFn: (payload: IUserLogin2FASendRequest) => {
      return UserServiceAPIs().userLogin2FAVerificationSend(payload);
    },
    onSuccess: () => {
      navigate('/auth/verification', { state: { loginData: loginDataToBePassed } });
    },
  });

  const loginApi = useMutation({
    mutationFn: (payload: IUserLoginRequest) => {
      return UserServiceAPIs().userLogin(payload);
    },

    onSuccess: ({ data: response }) => {
      console.log('response', response);
      if (response.userStatus === "KYC_PENDING" && portal !== PortalNameType.ADVISOR) {
        setError(getErrorMessage('U010111'));
        setTimeout(() => setError(''), 5000);
        return;
      }
      setLoginDataToBePassed(response);
      verification2FaSendApi.mutateAsync({
        ...response,
        isRequestedForEmail: false,
      });
    },
    onError: (error: IGenericErrorResponse) => {
      if (error.response?.data?.error?.errorCode === 'U010015') {
        navigate('/auth/change-password', { state: { userNameOrEmail: methods.watch('userNameOrEmail') } });
      }
      if (error.response?.data?.error?.errorCode === 'U010014') {
        setIsResetPassword(true);
      }
      if (error.response?.data?.error?.errorCode === 'U010093') {
        setError(error.response?.data?.error?.errorDetails);
      } else {
        setError(getErrorMessage(error.response?.data?.error?.errorCode));
      }
      setTimeout(() => {
        if (error.response?.data?.error?.errorCode !== 'U010014') {
          setError('');
        }
      }, 5000);
    },
  });


  const sendResetRequest = useMutation({
    mutationFn: (payload: ISubmitResetPasswordRequest) => UserServiceAPIs().submitResetPasswordRequestApi(payload),

    onSuccess: () => {
      setSuccess(true);
      setError('');
    },

    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const LoginSchema = object().shape({
    userNameOrEmail: fieldValidations.userNameOrEmail,
    password: fieldValidations.passwordLogin,
  });

  const ResetPasswordSchema = object().shape({
    userNameOrEmail: fieldValidations.userNameOrEmail,
  });

  const defaultValues = {
    userNameOrEmail: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const resetmethods = useForm({
    resolver: yupResolver(ResetPasswordSchema),
    defaultValues,
  });

  const { handleSubmit } = methods;

  const onSubmit = async (data: ILogin) => {
    const loginPayload = {
      userName: data.userNameOrEmail,
      password: data.password,
    };

    loginApi.mutateAsync(loginPayload);
  };

  const resetOnSubmit = async (data: { userNameOrEmail: string }) => {
    const resetPayload = {
      emailOrUserName: data.userNameOrEmail,
      emailSession: '',
      mobileSession: '',
      validateMobileEmailSession: false,
    };
    console.log('resetPayload', resetPayload);
    sendResetRequest.mutateAsync(resetPayload);
  };

  return (
    <>
      <Typography variant="h3">Login to your account</Typography>
      <Typography variant="body1" color={theme.palette.text.disabled}>
        Please login using your username and password
      </Typography>
      {isResetPassword ? (
        <FormProvider methods={resetmethods} onSubmit={resetmethods?.handleSubmit(resetOnSubmit)}>
          {error && !success && (
            <Alert variant="filled" severity="error" sx={{ width: '100%', mb: -3, mt: 2 }}>
              <Typography variant="body2" color="text.white">
                {error}
              </Typography>
            </Alert>
          )}

          {success && !error && (
            <Alert variant="filled" severity="success" sx={{ width: '100%', mb: -3, mt: 2 }}>
              <Typography variant="body2" color="text.white" id="password-reset-message">
                Your request to reset password has been sent to Administrator. You will receive an email incase your request has been authorised or rejected.
              </Typography>
            </Alert>
          )}
          <Stack spacing={5} marginTop={5}>
            <RHFTextField name="userNameOrEmail" label="Username" />

            <Button disabled={sendResetRequest.isPending || success} fullWidth color="primary" size="large" type="submit" variant="contained">
              Reset Password
            </Button>
          </Stack>
        </FormProvider>
      ) : (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: -3, mt: 2 }}>
              <Typography variant="body2" color="error" id="user-error">
                {error}
              </Typography>
            </Alert>
          )}
          <Stack spacing={5} marginTop={5}>
            <Stack>
              <RHFTextField name="userNameOrEmail" label="Username" id="userNameOrEmail" />

              {portal === PortalNameType.ADVISOR && (
                <Button
                  variant="text"
                  color="primary"
                  sx={{ width: 'fit-content', alignSelf: 'flex-end' }}
                  onClick={() => navigate('/auth/forgot-username')}
                  id="forgotUsername"
                >
                  Forgot Username?
                </Button>
              )}
            </Stack>

            <Stack>
              <RHFTextField
                name="password"
                label="Password"
                id="password"
                type={password.value ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={password.onToggle} edge="end">
                        <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="text"
                color="primary"
                sx={{ width: 'fit-content', alignSelf: 'flex-end' }}
                onClick={() => navigate('/auth/forgot-password')}
                id="forgotPassword"
              >
                Forgot Password?
              </Button>
            </Stack>

            <Button disabled={loginApi.isPending} fullWidth color="primary" size="large" type="submit" variant="contained" id="login">
              {loginApi.isPending ? 'Logging in...' : 'Login'}
            </Button>
            {registrationAllowed && (
              <Box display="flex" alignItems="center">
                <Typography variant="body1">Don’t have an account?</Typography>

                <Button variant="text" color="primary" onClick={() => {
                  trackEvent('registration_cta_click', { app: 'advisor' });
                  navigate('/signup/accounts/terms');
                }} id="registerAccount">
                  Register account
                </Button>
              </Box>
            )}

          </Stack>
        </FormProvider>
      )}
    </>
  );
};

export default LoginPageCommon;
